import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { CheckCircle, Upload, FileText, AlertCircle, Download } from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';
import { API_BASE_URL } from '../config';

type ImportPurpose = 'import' | 'blacklist' | 'unsubscribe';
type GeoCode = 'NO' | 'DE' | 'SE' | 'FI';

interface ImportHistory {
    id: string;
    timestamp: string;
    purpose: ImportPurpose;
    geo?: GeoCode;
    rowCount: number;
    status: 'success' | 'error';
}

export default function ImportsPage() {
    const [purpose, setPurpose] = useState<ImportPurpose>('import');
    const [geo, setGeo] = useState<GeoCode>('NO');
    const [file, setFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState<string | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<string | null>(null);

    // Mock history - would be fetched from API
    const [importHistory] = useState<ImportHistory[]>([
        {
            id: '1',
            timestamp: '2026-01-28 09:30',
            purpose: 'import',
            geo: 'NO',
            rowCount: 1234,
            status: 'success'
        },
        {
            id: '2',
            timestamp: '2026-01-27 14:15',
            purpose: 'blacklist',
            rowCount: 45,
            status: 'success'
        }
    ]);

    const requiresGeo = purpose === 'import';

    const getFormatHelp = () => {
        switch (purpose) {
            case 'import':
                return 'email, tag (Opener/Clicker/Inactive/New)';
            case 'blacklist':
                return 'email, reason';
            case 'unsubscribe':
                return 'email';
            default:
                return '';
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile && droppedFile.name.endsWith('.csv')) {
            setFile(droppedFile);
            setUploadError(null);
        } else {
            setUploadError('Please upload a CSV file');
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            setUploadError(null);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            setUploadError('Please select a file');
            return;
        }

        if (requiresGeo && !geo) {
            setUploadError('Please select a geography');
            return;
        }

        setUploading(true);
        setUploadError(null);
        setUploadResult(null);
        setUploadProgress('Reading file...');

        try {
            // Read and parse CSV client-side
            const text = await file.text();
            const lines = text.split(/\r?\n/).filter(line => line.trim());

            if (lines.length < 2) {
                throw new Error('CSV file is empty or has no data rows');
            }

            // Auto-detect delimiter (semicolon or comma)
            const header = lines[0].toLowerCase();
            const delimiter = header.includes(';') ? ';' : ',';
            const headers = header.split(delimiter).map(h => h.trim());

            const emailIdx = headers.indexOf('email');
            const tagIdx = headers.indexOf('tag');

            if (emailIdx === -1) {
                throw new Error('CSV must have an "email" column');
            }

            // Parse all rows
            const subscribers: { email: string; tag: string }[] = [];
            for (let i = 1; i < lines.length; i++) {
                const cols = lines[i].split(delimiter);
                const email = cols[emailIdx]?.trim();
                const tag = tagIdx !== -1 ? cols[tagIdx]?.trim() || 'New' : 'New';
                if (email && email.includes('@')) {
                    subscribers.push({ email, tag });
                }
            }

            if (subscribers.length === 0) {
                throw new Error('No valid email rows found in CSV');
            }

            setUploadProgress(`Parsed ${subscribers.length.toLocaleString()} subscribers. Uploading...`);

            // Send in batches of 2000
            const BATCH_SIZE = 2000;
            let totalProcessed = 0;

            for (let i = 0; i < subscribers.length; i += BATCH_SIZE) {
                const batch = subscribers.slice(i, i + BATCH_SIZE);
                const batchNum = Math.floor(i / BATCH_SIZE) + 1;
                const totalBatches = Math.ceil(subscribers.length / BATCH_SIZE);

                setUploadProgress(
                    `Processing batch ${batchNum}/${totalBatches} (${totalProcessed.toLocaleString()}/${subscribers.length.toLocaleString()})...`
                );

                const response = await fetch(`${API_BASE_URL}/api/imports/subscribers`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        subscribers: batch,
                        geo: geo.toLowerCase(),
                    }),
                });

                if (!response.ok) {
                    const errData = await response.json().catch(() => ({}));
                    throw new Error(errData.error || `Batch ${batchNum} failed: ${response.statusText}`);
                }

                const result = await response.json();
                totalProcessed += result.count || 0;
            }

            setUploadProgress(null);
            setUploadResult(
                `Successfully processed ${totalProcessed.toLocaleString()} of ${subscribers.length.toLocaleString()} subscribers`
            );
            setFile(null);

        } catch (error) {
            setUploadProgress(null);
            setUploadError(error instanceof Error ? error.message : 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const downloadSampleCSV = () => {
        let csvContent = '';

        switch (purpose) {
            case 'import':
                csvContent = 'email,tag\nuser@gmail.com,Opener\nuser2@outlook.com,Clicker\n';
                break;
            case 'blacklist':
                csvContent = 'email,reason\nspam@example.com,Spam complaint\nbad@test.com,Manual block\n';
                break;
            case 'unsubscribe':
                csvContent = 'email\nuser@unsubscribe.com\n';
                break;
        }

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sample_${purpose}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    return (
        <MainLayout>
            <div className="space-y-6">
                {/* Step 1: Purpose Selection */}
                <Card className="shadow-lg border-gray-200">
                    <CardHeader className="pb-4">
                        <h2 className="text-xl font-semibold">1. Select Import Purpose</h2>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                                <input
                                    type="radio"
                                    name="purpose"
                                    value="import"
                                    checked={purpose === 'import'}
                                    onChange={(e) => setPurpose(e.target.value as ImportPurpose)}
                                    className="w-4 h-4 text-blue-600"
                                />
                                <div className="ml-3">
                                    <div className="font-semibold text-gray-900">Import Subscribers</div>
                                    <div className="text-sm text-gray-600">Enrich and add to database</div>
                                </div>
                            </label>

                            <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-red-50 transition-colors">
                                <input
                                    type="radio"
                                    name="purpose"
                                    value="blacklist"
                                    checked={purpose === 'blacklist'}
                                    onChange={(e) => setPurpose(e.target.value as ImportPurpose)}
                                    className="w-4 h-4 text-red-600"
                                />
                                <div className="ml-3">
                                    <div className="font-semibold text-gray-900">Blacklist Emails</div>
                                    <div className="text-sm text-gray-600">Block from all geographies</div>
                                </div>
                            </label>

                            <label className="flex items-center p-4 border rounded-lg cursor-not-allowed opacity-50 bg-gray-50">
                                <input
                                    type="radio"
                                    name="purpose"
                                    value="unsubscribe"
                                    disabled
                                    className="w-4 h-4 text-gray-400"
                                />
                                <div className="ml-3">
                                    <div className="font-semibold text-gray-700">Unsubscribe</div>
                                    <div className="text-sm text-gray-500">Mark as unsubscribed (Coming Soon)</div>
                                </div>
                            </label>
                        </div>
                    </CardContent>
                </Card>

                {/* Step 2: Geography Selection */}
                {requiresGeo && (
                    <Card className="shadow-lg border-gray-200">
                        <CardHeader className="pb-4">
                            <h2 className="text-xl font-semibold">2. Select Geography</h2>
                        </CardHeader>
                        <CardContent>
                            <select
                                value={geo}
                                onChange={(e) => setGeo(e.target.value as GeoCode)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="NO">Norway (NO)</option>
                                <option value="DE">Germany (DE)</option>
                                <option value="SE">Sweden (SE)</option>
                                <option value="FI">Finland (FI)</option>
                            </select>
                        </CardContent>
                    </Card>
                )}

                {!requiresGeo && (
                    <Card className="shadow-lg border-gray-200 bg-blue-50">
                        <CardContent className="py-4">
                            <div className="flex items-center gap-2 text-sm text-blue-800">
                                <AlertCircle className="w-4 h-4" />
                                <span>Geography selection not required for {purpose}</span>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Step 3: File Upload */}
                <Card className="shadow-lg border-gray-200">
                    <CardHeader className="pb-4">
                        <h2 className="text-xl font-semibold">
                            {requiresGeo ? '3' : '2'}. Upload CSV File
                        </h2>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Drag & Drop Area */}
                        <div
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragging
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-300 hover:border-gray-400'
                                }`}
                        >
                            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                            <p className="text-gray-600 mb-2">
                                Drag & drop CSV here or click to browse
                            </p>
                            <input
                                type="file"
                                accept=".csv"
                                onChange={handleFileSelect}
                                className="hidden"
                                id="file-input"
                            />
                            <label
                                htmlFor="file-input"
                                className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors"
                            >
                                Choose File
                            </label>
                        </div>

                        {/* Format Help */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm font-medium text-gray-700 mb-1">Expected format:</p>
                            <p className="text-sm text-gray-600 font-mono">{getFormatHelp()}</p>
                        </div>

                        {/* Selected File */}
                        {file && (
                            <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-green-600" />
                                    <span className="text-sm font-medium text-green-900">{file.name}</span>
                                    <span className="text-xs text-green-700">
                                        ({(file.size / 1024).toFixed(1)} KB)
                                    </span>
                                </div>
                                <button
                                    onClick={() => setFile(null)}
                                    className="text-green-700 hover:text-green-900 font-bold"
                                >
                                    ×
                                </button>
                            </div>
                        )}

                        {/* Progress */}
                        {uploadProgress && (
                            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                <span className="text-sm text-blue-900">{uploadProgress}</span>
                            </div>
                        )}

                        {/* Error Message */}
                        {uploadError && (
                            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <AlertCircle className="w-5 h-5 text-red-600" />
                                <span className="text-sm text-red-900">{uploadError}</span>
                            </div>
                        )}

                        {/* Success Message */}
                        {uploadResult && (
                            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <span className="text-sm text-green-900">{uploadResult}</span>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                            <button
                                onClick={downloadSampleCSV}
                                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                <Download className="w-4 h-4" />
                                Download Sample CSV
                            </button>
                            <button
                                onClick={handleUpload}
                                disabled={!file || uploading}
                                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-semibold"
                            >
                                <Upload className="w-5 h-5" />
                                {uploading ? 'Processing...' : 'Process Import →'}
                            </button>
                        </div>
                    </CardContent>
                </Card>

                {/* Recent Imports History */}
                <Card className="shadow-lg border-gray-200">
                    <CardHeader className="pb-4">
                        <h2 className="text-xl font-semibold">Recent Imports</h2>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {importHistory.map((item) => (
                                <div
                                    key={item.id}
                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        {item.status === 'success' ? (
                                            <CheckCircle className="w-5 h-5 text-green-600" />
                                        ) : (
                                            <AlertCircle className="w-5 h-5 text-red-600" />
                                        )}
                                        <div>
                                            <span className="text-sm text-gray-600">{item.timestamp}</span>
                                            <span className="mx-2 text-gray-400">•</span>
                                            <span className="text-sm font-medium text-gray-900 capitalize">
                                                {item.purpose}
                                            </span>
                                            {item.geo && (
                                                <>
                                                    <span className="mx-2 text-gray-400">•</span>
                                                    <span className="text-sm text-gray-600">({item.geo})</span>
                                                </>
                                            )}
                                            <span className="mx-2 text-gray-400">•</span>
                                            <span className="text-sm text-gray-600">
                                                {item.rowCount.toLocaleString()} rows
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </MainLayout>
    );
}
